import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import App from './App.jsx'
import './index.css'
import { registerSW } from 'virtual:pwa-register'
import { Capacitor } from '@capacitor/core'
import { registerDeepLinkListener } from './utils/platform/deeplink'

// i18n 초기화 (다국어 지원)
import './i18n'

// Capacitor 환경 감지
const isCapacitor = !!(window.Capacitor || window.capacitor);

console.log('🚀 앱 시작:', isCapacitor ? 'Capacitor (iOS)' : '웹');

// 웹 브라우저 환경에서만 safe area CSS 적용을 위한 클래스 추가
if (!isCapacitor) {
  document.body.classList.add('web-browser');
  console.log('🌐 웹 브라우저 감지 - safe-area CSS 활성화');
} else {
  console.log('📱 네이티브 앱 감지 - safe-area CSS 비활성화');
}

// iOS Safari viewport 높이 동적 조정
function adjustViewportHeight() {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  if (isMobile) {
    let initialViewportHeight = window.innerHeight;
    let initialSafeAreaBottom = 0;
    
    // safe area bottom 값 가져오기
    function getSafeAreaBottom() {
      const testElement = document.createElement('div');
      testElement.style.paddingBottom = 'env(safe-area-inset-bottom)';
      document.body.appendChild(testElement);
      const computedStyle = window.getComputedStyle(testElement);
      const safeAreaBottom = parseInt(computedStyle.paddingBottom) || 0;
      document.body.removeChild(testElement);
      return safeAreaBottom;
    }
    
    // 초기 safe area 값 설정
    setTimeout(() => {
      initialSafeAreaBottom = getSafeAreaBottom();
    }, 100);
    
    window.addEventListener('resize', () => {
      const currentHeight = window.innerHeight;
      const heightDifference = Math.abs(currentHeight - initialViewportHeight);
      
      // iOS에서 주소창 숨김/표시로 인한 높이 변화 처리 (150px 이하 변화)
      if (heightDifference < 150) {
        // CSS의 100dvh와 env(safe-area-inset-bottom)를 활용
        const availableHeight = currentHeight - initialSafeAreaBottom;
        
        // CSS custom property로 실제 사용 가능한 높이 전달
        document.documentElement.style.setProperty('--real-viewport-height', `${availableHeight}px`);
        document.documentElement.style.setProperty('--safe-area-bottom', `${initialSafeAreaBottom}px`);
      } else {
        // 큰 변화는 기기 회전 등이므로 초기값 재설정
        initialViewportHeight = currentHeight;
        initialSafeAreaBottom = getSafeAreaBottom();
      }
    });
  }
}

// 화면 회전 제어 (세로 모드만 허용)
function lockToPortraitMode() {
  // 모바일 디바이스에서만 실행
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  if (isMobile) {
    // Screen Orientation API 지원 확인 및 세로 모드 강제
    if (screen.orientation && screen.orientation.lock) {
      try {
        screen.orientation.lock('portrait').catch((error) => {
          console.log('Portrait lock failed:', error);
        });
      } catch (error) {
        console.log('Screen orientation lock not supported:', error);
      }
    }
    
    // iOS Safari 대응 - meta 태그로 이미 설정했지만 추가 체크
    if (window.DeviceOrientationEvent) {
      window.addEventListener('orientationchange', function() {
        // 가로 모드 감지 시 알림 (CSS로 이미 처리되지만 추가 방어)
        if (Math.abs(window.orientation) === 90) {
          console.log('Landscape mode detected - please rotate to portrait');
        }
      });
    }
    
    // Fullscreen API 대응 (PWA 모드에서)
    if (document.documentElement.requestFullscreen) {
      // 필요 시 전체화면 모드에서도 세로 모드 유지
      document.addEventListener('fullscreenchange', function() {
        if (document.fullscreenElement && screen.orientation && screen.orientation.lock) {
          screen.orientation.lock('portrait').catch((error) => {
            console.log('Portrait lock in fullscreen failed:', error);
          });
        }
      });
    }
  }
}

// DOM 로드 후 초기화
document.addEventListener('DOMContentLoaded', () => {
  adjustViewportHeight();
  lockToPortraitMode();
});

// 앱 렌더링 (Capacitor, 웹 환경 모두 동일)
const root = ReactDOM.createRoot(document.getElementById('root'));

// ✅ [Android FCM] React 준비 상태 확인을 위한 window 객체 설정
window.ReactDOMRoot = root;

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <App />
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>,
)

// 서비스 워커 등록 (웹 환경에서만)
if (!isCapacitor && 'serviceWorker' in navigator) {
  registerSW()
}

if (Capacitor.isNativePlatform()) {
  registerDeepLinkListener();
}
